import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import { apiHelper } from '@/helpers/index';

Vue.use(Vuex);

Vue.config.productionTip = false; //Prevent warnings during tests
Vue.config.devtools = false;

const state = {
  associations: [],
  from: 0,
  total: 0,
  currentPage: 1,
  fullText: '',
  categories: [],
  facets: {},
  facetIds: [],
  loading: false,
  ITEMS_PER_PAGE: 12,
};

export const mutations = {
  setAssociations(state, associations) {
    state.associations = associations;
  },
  setTotal(state, value) {
    state.total = value;
  },
  setFullText(state, fullText) {
    state.fullText = fullText;
  },
  setFrom(state, from) {
    state.from = from;
  },
  setCurrentPage(state, page) {
    state.currentPage = page;
  },
  setCategories(state, aggregations) {
    const { categories, facets, facetIds } = normalize(aggregations);

    // keep currently checked facets
    for (const [key, value] of Object.entries(state.facets)) {
      if (value.checked) {
        facets[key] = {
          categorieId: value.categorieId,
          checked: true,
          count: (facets[key] && facets[key].count) || 0,
        };
        // add the checked facet to filters if not exists
        if (!facetIds.includes(key)) {
          facetIds.push(key);
        }
      }
    }

    state.categories = categories.sort();
    state.facets = facets;
    state.facetIds = facetIds;
  },
  setFacet(state, { key, checked }) {
    state.facets[key].checked = checked;
  },
  resetFacets(state) {
    for (const facetId of state.facetIds) {
      state.facets[facetId].checked = false;
    }
  },
  setLoading(state, isLoading) {
    state.loading = isLoading;
  },
};

export const getters = {
  getFacetsByCategorie: (state) => (categorieId) => {
    const facets = [];
    for (const facetKey of state.facetIds) {
      const facet = state.facets[facetKey];
      if (facet.categorieId === categorieId && facetKey.length > 0)
        facets.push({ categorieId, key: facetKey, count: facet.count, checked: facet.checked });
    }
    return facets;
  },
  getFacet: (state) => (facetId) => {
    return state.facets[facetId];
  },
  /**
   * This method is used to build the ES query filters
   * It retrieves every checked facet and build an object with the following structure
   * 'filters' : {"activites" : ['sport','...'], "quartiers": ['pledran', '...'], 'fulltext': 'le rugby'}
   */
  getQueryFilters: (state) => {
    const filters = {};
    for (const facetKey of state.facetIds) {
      const facet = state.facets[facetKey];
      if (facet.checked) {
        if (!filters[facet.categorieId]) filters[facet.categorieId] = [];
        filters[facet.categorieId].push(facetKey);
      }
    }
    if (state.fullText) filters.fullText = state.fullText;
    return filters;
  },
  getCheckedFacets: (state) => {
    const checkedFacets = [];
    for (const facetId of state.facetIds) {
      const facet = state.facets[facetId];
      if (facet.checked) checkedFacets.push(facet);
    }
    return checkedFacets;
  },
};

export const actions = {
  async search({ commit, state, getters }, newSearch = false) {
    commit('setLoading', true);
    if (newSearch) commit('setCurrentPage', 1);
    const query = Object.create(null);
    const filters = getters.getQueryFilters;
    if (Object.keys(filters).length > 0) {
      query.filters = filters;
    }
    query.size = state.ITEMS_PER_PAGE;
    query.from = (state.currentPage - 1) * state.ITEMS_PER_PAGE;
    const url = apiHelper.buildURL('/ui/associations/search');
    const response = await axios.post(url, query);
    const associations = response.data.hits.hits.map((item) => item._source);
    const total = response.data.hits.total.value;
    commit('setAssociations', associations);
    commit('setTotal', total);
    commit('setCategories', response.data.aggregations);
    commit('setLoading', false);
  },
  setFullText({ commit, dispatch }, fullText) {
    commit('setFullText', fullText);
    dispatch('search', true);
  },
  setCurrentPage({ commit, dispatch }, page) {
    commit('setCurrentPage', page);
    dispatch('search');
  },
  setFacet({ commit, dispatch }, { key, checked = false }) {
    commit('setFacet', { key, checked });
    dispatch('search', true);
  },
  resetFacets({ commit, dispatch }) {
    commit('resetFacets');
    dispatch('search', true);
  },
};

const store = new Vuex.Store({
  state,
  mutations,
  actions,
  getters,
  strict: process.env.NODE_ENV !== 'production',
});

export default store;

/**
 * This method is used to normalize the data that elastic returns
 * The point is to avoid nested object for the vue reactive system.
 *
 * facetIds: ['pledran', 'sport', 'honfleur', '..',] : a list of all facet keys
 * categories: ['villes', 'quartier', '..',] : a list of all aggregation keys
 * facets: {"facetId" : {categorieId, count, checked}, "..": {.., .., ..}} : the normalized facet objects
 * @see https://forum.vuejs.org/t/vuex-best-practices-for-complex-objects/10143#post_2
 * @param {Object} aggregations The aggregation object that comes from elastic
 */
export const normalize = function(aggregations) {
  const categories = [];
  const facets = {};
  const facetIds = [];
  for (const key of Object.keys(aggregations)) {
    //! Normalize the data
    categories.push(key);
    aggregations[key].buckets.forEach((facet) => {
      if (facet.doc_count > 0) {
        facets[facet.key] = { categorieId: key, count: facet.doc_count, checked: false };
        facetIds.push(facet.key);
      }
    });
  }
  return { categories, facets, facetIds };
};
